import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import { EditModeContext, EditableText } from './EditableContent';
import { Button } from './ui/button';
import { ArrowRight, CheckCircle } from 'lucide-react';

/**
 * Generic CMS Block Renderer - renders blocks saved via the Page Builder.
 * Include this on any page to render additional CMS-managed content.
 * 
 * Blocks that are already handled by a page's own layout (e.g. 'hero' on HomePage)
 * can be excluded via the `excludeTypes` prop.
 */
export default function CMSBlockRenderer({ excludeTypes = [] }) {
  const { pageContent, setPageContent, editMode } = useContext(EditModeContext);

  if (!pageContent?.blocks?.length) return null;

  const blocks = pageContent.blocks.filter(b => !excludeTypes.includes(b.type));
  if (!blocks.length) return null;

  const updateBlockContent = (blockId, updates) => {
    if (!pageContent) return;
    const idx = pageContent.blocks.findIndex(b => b.id === blockId);
    if (idx >= 0) {
      const newBlocks = [...pageContent.blocks];
      newBlocks[idx] = { ...newBlocks[idx], content: { ...newBlocks[idx].content, ...updates } };
      setPageContent({ ...pageContent, blocks: newBlocks });
    }
  };

  return (
    <>
      {blocks.map((block) => (
        <div key={block.id || block.type} className={editMode ? 'relative outline-dashed outline-1 outline-zinc-700 rounded-lg' : ''}>
          {editMode && (
            <span className="absolute -top-3 left-4 text-[10px] bg-zinc-800 text-zinc-400 px-2 py-0.5 rounded-sm uppercase tracking-wider z-10">
              {block.type.replace(/_/g, ' ')}
            </span>
          )}
          <BlockContent block={block} onUpdate={updateBlockContent} />
        </div>
      ))}
    </>
  );
}

function BlockContent({ block, onUpdate }) {
  const c = block.content || {};

  switch (block.type) {
    case 'hero':
      return (
        <section className="py-16 bg-zinc-900/50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <EditableText
              value={c.title || 'Section Title'}
              onChange={(val) => onUpdate(block.id, { title: val })}
              tag="h2"
              className="text-3xl sm:text-4xl font-black text-white uppercase tracking-tight"
            />
            {c.subtitle && (
              <EditableText
                value={c.subtitle}
                onChange={(val) => onUpdate(block.id, { subtitle: val })}
                tag="p"
                className="text-zinc-400 mt-3 max-w-2xl mx-auto text-lg"
              />
            )}
          </div>
        </section>
      );

    case 'text':
      return (
        <section className="py-12">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            {c.title && (
              <EditableText
                value={c.title}
                onChange={(val) => onUpdate(block.id, { title: val })}
                tag="h2"
                className="text-2xl font-bold text-white mb-4"
              />
            )}
            <EditableText
              value={c.body || c.text || 'Add your text content here...'}
              onChange={(val) => onUpdate(block.id, { body: val })}
              tag="p"
              className="text-zinc-400 leading-relaxed whitespace-pre-line"
              multiline
            />
          </div>
        </section>
      );

    case 'image':
      return (
        <section className="py-12">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            {c.url && (
              <img
                src={c.url}
                alt={c.alt || ''}
                className="w-full rounded-lg object-cover max-h-[500px]"
              />
            )}
            {c.caption && (
              <p className="text-zinc-500 text-sm text-center mt-3">{c.caption}</p>
            )}
          </div>
        </section>
      );

    case 'image_text':
      return (
        <section className="py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className={`grid md:grid-cols-2 gap-12 items-center ${c.image_right ? '' : 'direction-rtl'}`}>
              <div>
                {c.title && (
                  <EditableText
                    value={c.title}
                    onChange={(val) => onUpdate(block.id, { title: val })}
                    tag="h2"
                    className="text-2xl font-bold text-white mb-4"
                  />
                )}
                <EditableText
                  value={c.body || 'Add your text content here...'}
                  onChange={(val) => onUpdate(block.id, { body: val })}
                  tag="p"
                  className="text-zinc-400 leading-relaxed"
                  multiline
                />
              </div>
              <div>
                {c.image_url && (
                  <img src={c.image_url} alt={c.title || ''} className="w-full rounded-lg object-cover" />
                )}
              </div>
            </div>
          </div>
        </section>
      );

    case 'banner':
      return (
        <section
          className="py-16"
          style={{ backgroundColor: c.bg_color || '#78BE20' }}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <EditableText
              value={c.title || 'Banner Title'}
              onChange={(val) => onUpdate(block.id, { title: val })}
              tag="h2"
              className="text-3xl font-black text-black uppercase tracking-tight"
            />
            {c.subtitle && (
              <EditableText
                value={c.subtitle}
                onChange={(val) => onUpdate(block.id, { subtitle: val })}
                tag="p"
                className="text-black/70 mt-2 text-lg"
              />
            )}
            {c.button_text && (
              <Link to={c.button_url || '#'}>
                <Button className="mt-6 bg-black text-white hover:bg-zinc-800">
                  {c.button_text} <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            )}
          </div>
        </section>
      );

    case 'trade_cta':
      return (
        <section className="py-16 bg-[#78BE20]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row items-center justify-between gap-8">
            <div>
              <EditableText
                value={c.title || 'Trade Pricing Available'}
                onChange={(val) => onUpdate(block.id, { title: val })}
                tag="h2"
                className="text-3xl font-black text-black uppercase tracking-tight"
              />
              <EditableText
                value={c.subtitle || 'Register for a trade account to access wholesale pricing.'}
                onChange={(val) => onUpdate(block.id, { subtitle: val })}
                tag="p"
                className="text-black/70 mt-2 text-lg"
              />
            </div>
            <Link to={c.button_url || '/trade-application'}>
              <Button className="bg-black text-white hover:bg-zinc-800 text-lg px-8 py-6">
                {c.button_text || 'Apply Now'} <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </section>
      );

    case 'stats':
      return (
        <section className="py-12 bg-zinc-900/50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
              {(c.items || []).map((stat, i) => (
                <div key={i}>
                  <div className="text-3xl font-black text-[#78BE20]">{stat.value}</div>
                  <div className="text-zinc-400 text-sm mt-1">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>
      );

    case 'features':
      return (
        <section className="py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {c.title && (
              <h2 className="text-2xl font-bold text-white text-center mb-8">{c.title}</h2>
            )}
            <div className="grid md:grid-cols-3 gap-8">
              {(c.items || []).map((item, i) => (
                <div key={i} className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-6">
                  <CheckCircle className="h-6 w-6 text-[#78BE20] mb-3" />
                  <h3 className="font-bold text-white mb-2">{item.title}</h3>
                  <p className="text-zinc-400 text-sm">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      );

    case 'video':
      return (
        <section className="py-12">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            {c.title && <h2 className="text-2xl font-bold text-white text-center mb-6">{c.title}</h2>}
            {c.url && (
              <div className="aspect-video bg-zinc-900 rounded-lg overflow-hidden">
                <iframe
                  src={c.url.replace('watch?v=', 'embed/').replace('youtu.be/', 'youtube.com/embed/')}
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  title={c.title || 'Video'}
                />
              </div>
            )}
          </div>
        </section>
      );

    case 'faq':
      return (
        <section className="py-12">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            {c.title && <h2 className="text-2xl font-bold text-white text-center mb-8">{c.title}</h2>}
            <div className="space-y-4">
              {(c.items || []).map((item, i) => (
                <details key={i} className="bg-zinc-900/50 border border-zinc-800 rounded-sm group">
                  <summary className="cursor-pointer p-4 font-medium text-white group-open:text-[#78BE20]">
                    {item.question}
                  </summary>
                  <div className="px-4 pb-4 text-zinc-400 text-sm">{item.answer}</div>
                </details>
              ))}
            </div>
          </div>
        </section>
      );

    case 'divider':
      return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <hr className="border-zinc-800 my-8" />
        </div>
      );

    case 'spacer':
      return <div style={{ height: `${c.height || 60}px` }} />;

    default:
      return null;
  }
}
